import { logger } from 'src/helpers/logger';
import { FINDING_SEVERITIES, VALUE_KINDS } from './constants';
import type { FindingSeverity, ValueKind } from './constants';
import { parseStrictFloat } from '../rateEngine/editRateEngine/ruleForms/numericInput';
import { toNumberOrZero } from './helpers';
import type {
  IComponentRow,
  IFinding,
  IFormData,
  IHeadline,
  IRateSimulatorInput,
  IRateSimulatorResult,
  IResolvedVersions,
  IResultInterval,
  IRunPayload,
  IValueAmount,
} from './types';

// Parsing is deliberately tolerant: anything missing degrades to a safe default rather than throwing.

type Raw = Record<string, unknown>;

const asRecord = (value: unknown): Raw => (value && typeof value === 'object' ? (value as Raw) : {});

const asArray = (value: unknown): Raw[] => (Array.isArray(value) ? value.map(asRecord) : []);

const asString = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

const asNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string') {
    const parsed = Number(value);

    if (Number.isFinite(parsed)) return parsed;
  }

  return 0;
};

const asValueKind = (value: unknown, fallback: ValueKind): ValueKind => {
  const kinds = Object.values(VALUE_KINDS) as string[];

  return typeof value === 'string' && kinds.includes(value) ? (value as ValueKind) : fallback;
};

const toAmount = (raw: Raw, fallbackKind: ValueKind): IValueAmount => ({
  value: asNumber(raw.value),
  kind: asValueKind(raw.kind, fallbackKind),
});

// `null` rather than a zero amount: an unreadable total must not render as a priced zero. Key
// presence is not enough — `{ value: null }` arrives for a figure the run could not price.
const toOptionalAmount = (value: unknown, fallbackKind: ValueKind): IValueAmount | null => {
  const raw = asRecord(value);
  const parsed = typeof raw.value === 'number' ? raw.value : Number(raw.value);

  if (raw.value === null || raw.value === undefined || raw.value === '' || !Number.isFinite(parsed)) return null;

  return toAmount(raw, fallbackKind);
};

const toComponentRow = (raw: Raw, index: number): IComponentRow => ({
  id: asString(raw.id) ?? `row-${index}`,
  label: asString(raw.rule_name) ?? '',
  basis: asString(raw.basis),
  amount: toAmount(asRecord(raw.amount), VALUE_KINDS.MONEY_PER_HOUR),
});

const toInterval = (raw: Raw, index: number): IResultInterval => {
  const total = asRecord(raw.total);

  return {
    id: asString(raw.id) ?? `interval-${index}`,
    startTime: asString(raw.start_time),
    endTime: asString(raw.end_time),
    rows: asArray(raw.rows).map(toComponentRow),
    total: {
      label: asString(total.label) ?? '',
      amount: toAmount(asRecord(total.amount), VALUE_KINDS.MONEY_PER_HOUR),
    },
  };
};

// An unrecognised severity stops the preview rather than letting unverified figures through.
const toSeverity = (value: unknown): FindingSeverity =>
  value === FINDING_SEVERITIES.ADVISORY ? FINDING_SEVERITIES.ADVISORY : FINDING_SEVERITIES.BLOCKING;

// The wire's `source` is deliberately not read: the message it would caption already names it.
const toFinding = (raw: Raw, index: number): IFinding => ({
  id: asString(raw.id) ?? `finding-${index}`,
  severity: toSeverity(raw.severity),
  message: asString(raw.message) ?? '',
});

// The engine is nested inside its version — `V3` alone names no engine. A blocked run resolves no card
// and an unresolved version nulls its whole object; a version without its engine predates the nesting.
const toVersions = (raw: Raw): IResolvedVersions => {
  const engineVersion = asRecord(raw.engine_version);

  return {
    engine: asString(asRecord(engineVersion.engine).name),
    engineVersion: asString(engineVersion.name),
    rateCard: asString(asRecord(raw.rate_card).name),
  };
};

// Absent entirely on a blocked run, which prices nothing.
const toHeadline = (value: unknown): IHeadline | null => {
  if (!value || typeof value !== 'object') return null;

  const raw = asRecord(value);

  return {
    totalPay: toOptionalAmount(raw.total_pay, VALUE_KINDS.MONEY_TOTAL),
    totalBill: toOptionalAmount(raw.total_bill, VALUE_KINDS.MONEY_TOTAL),
  };
};

// Neither `asNumber` (fails to `0`, a valid boundary here) nor a bare `Number` (takes `true` and
// `[12]`): both turn unreadable input into a threshold that reads as Post-parity from day one.
const asWeekCount = (value: unknown): number | null => {
  const parsed =
    // eslint-disable-next-line no-nested-ternary
    typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;

  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

// A `null` boundary is served on purpose when the tenure gate is off. A column renamed away is absent
// rather than null, and an unreadable one is reshaped: either removes the parity indicator without a trace.
export function toFormData(response: unknown): IFormData {
  const parity = asRecord(asRecord(response).parity);
  const weeks = parity.qualifying_weeks;
  const postParityMinWeeks = asWeekCount(weeks);

  if (!('qualifying_weeks' in parity)) {
    logger.warn('[rateSimulator] form data carried no parity boundary at all');
  } else if (postParityMinWeeks === null && weeks !== null) {
    logger.warn(`[rateSimulator] form data carried an unreadable parity boundary: ${JSON.stringify(weeks)}`);
  }

  return { postParityMinWeeks };
}

export function toResult(response: unknown): IRateSimulatorResult {
  const raw = asRecord(response);

  return {
    payIntervals: asArray(raw.pay_intervals).map(toInterval),
    billIntervals: asArray(raw.bill_intervals).map(toInterval),
    versions: toVersions(asRecord(raw.resolved_versions)),
    headline: toHeadline(raw.headline),
    findings: asArray(raw.findings).map(toFinding),
  };
}

export const hasBlockingFinding = (result: IRateSimulatorResult): boolean =>
  result.findings.some(finding => finding.severity === FINDING_SEVERITIES.BLOCKING);

// The one place the form's names cross to the wire's. Both callers run behind a validity gate, so
// the guard below never fires; it is there so the guarantee lives in the return type, not a cast.
export function toRunPayload(
  input: IRateSimulatorInput,
  { tenureEnabled }: { tenureEnabled: boolean },
): IRunPayload | null {
  const { locationId, positionId, agencyId, shiftDate, startTime, endTime } = input;

  if (!locationId || !positionId || !agencyId || !shiftDate || !startTime || !endTime) return null;

  // The same strict parse the schema validated with. A bare `Number` would read "0x10" as 16 and
  // price a run off input the form had rejected.
  const workerAge = parseStrictFloat(input.age);
  // With tenure off the weeks price nothing and the endpoint does not ask for them.
  const serviceWeeks = tenureEnabled ? parseStrictFloat(input.tenureWeeks) : undefined;

  if (workerAge === null || serviceWeeks === null) return null;

  return {
    company_id: locationId,
    position_id: positionId,
    agency_id: agencyId,
    shift_date: shiftDate,
    shift_start_time: startTime,
    shift_end_time: endTime,
    worker_age: workerAge,
    ...(serviceWeeks === undefined ? {} : { length_of_service_weeks: serviceWeeks }),
    days_worked_this_week: toNumberOrZero(input.daysWorked),
    pension_opted_out: input.pensionOptedOut,
    hours_worked_to_date: {
      weekly: toNumberOrZero(input.hoursWorkedWeekly),
      bi_weekly: toNumberOrZero(input.hoursWorkedBiWeekly),
      daily: toNumberOrZero(input.hoursWorkedDaily),
    },
  };
}
